// API Client

import {
  FeedbackData,
  FeedbackMetadata,
  FeedbackResponse,
} from './types';

const API_URLS = {
  production: 'https://iipkklhhafrjesryscjh.supabase.co/functions/v1',
  staging: 'https://bssghvinezdawvupcyci.supabase.co/functions/v1',
} as const;

export type Environment = 'production' | 'staging';

export class TriaglyAPI {
  private apiUrl: string;
  private publishableKey: string;
  private getToken?: () => Promise<string>;
  private turnstileSiteKey: string;

  // Triagly's public Turnstile site key (safe to hardcode - client-side only)
  private static readonly DEFAULT_TURNSTILE_SITE_KEY = '0x4AAAAAAB8Dc-Fl964Vp1Nn';

  constructor(
    publishableKey: string,
    environment: Environment = 'production',
    apiUrl?: string,
    getToken?: () => Promise<string>,
    turnstileSiteKey?: string
  ) {
    // apiUrl override takes precedence, then environment-based URL
    this.apiUrl = (apiUrl || API_URLS[environment]).replace(/\/$/, ''); // Remove trailing slash
    this.publishableKey = publishableKey;
    this.getToken = getToken;
    // Always use Triagly's Turnstile site key (can be overridden for testing)
    this.turnstileSiteKey = turnstileSiteKey || TriaglyAPI.DEFAULT_TURNSTILE_SITE_KEY;
  }

  /**
   * Get the Turnstile site key
   */
  getTurnstileSiteKey(): string {
    return this.turnstileSiteKey;
  }

  /**
   * Get Turnstile token from widget if available
   */
  private async getTurnstileToken(): Promise<string | null> {
    // Check if Turnstile widget is available
    const turnstileWidget = document.querySelector('[data-turnstile-response]');
    if (turnstileWidget) {
      const token = turnstileWidget.getAttribute('data-turnstile-response');
      if (token) return token;
    }

    // Check if window.turnstile is available
    if ((window as any).turnstile) {
      try {
        // Get the first widget's response
        const widgets = document.querySelectorAll('.cf-turnstile');
        if (widgets.length > 0) {
          const widgetId = widgets[0].getAttribute('data-widget-id');
          if (widgetId) {
            const token = (window as any).turnstile.getResponse(widgetId);
            if (token) return token;
          }
        }
      } catch (error) {
        console.warn('Failed to get Turnstile token:', error instanceof Error ? error.message : 'Unknown error');
      }
    }

    return null;
  }

  /**
   * Submit feedback with new authentication
   */
  async submitFeedback(
    data: FeedbackData,
    metadata: FeedbackMetadata,
    turnstileToken?: string
  ): Promise<FeedbackResponse> {
    // Get Turnstile token if not provided
    if (!turnstileToken) {
      turnstileToken = await this.getTurnstileToken() || undefined;
    }

    // Only require Turnstile if configured
    if (this.turnstileSiteKey && !turnstileToken) {
      throw new Error('Turnstile verification required. Please complete the captcha.');
    }

    // Get hardened token if callback is provided
    let hardenedToken: string | undefined;
    if (this.getToken) {
      try {
        hardenedToken = await this.getToken();
      } catch (error) {
        console.error('Failed to get hardened token:', error instanceof Error ? error.message : 'Unknown error');
        throw new Error('Failed to authenticate. Please try again.');
      }
    }

    const payload = {
      publishableKey: this.publishableKey,
      title: data.title,
      description: data.description,
      metadata: {
        ...metadata,
        consoleLogs: data.consoleLogs,
      },
      tags: data.tags,
      reporterEmail: data.reporterEmail,
      reporterName: data.reporterName,
      turnstileToken,
      hardenedToken,
    };

    const response = await fetch(`${this.apiUrl}/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error', message: 'Unknown error' }));

      // Handle specific error types with user-friendly messages
      if (response.status === 401) {
        if (error.error === 'invalid_publishable_key') {
          throw new Error('Invalid API key. Please contact support.');
        } else if (error.error === 'token_required') {
          throw new Error('Authentication required. Please refresh and try again.');
        }
        throw new Error(error.message || 'Authentication failed');
      } else if (response.status === 403) {
        if (error.error === 'origin_not_allowed') {
          throw new Error('This website is not authorized to submit feedback.');
        }
        throw new Error(error.message || 'Access denied');
      } else if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const resetTime = retryAfter ? `in ${retryAfter} seconds` : 'later';
        throw new Error(`Too many requests. Please try again ${resetTime}.`);
      } else if (response.status === 400 && error.error === 'captcha_failed') {
        throw new Error('Captcha verification failed. Please try again.');
      }

      throw new Error(error.message || error.error || `Failed to submit feedback (HTTP ${response.status})`);
    }

    return await response.json();
  }
}
